import axios from "axios";
import { arcgisToGeoJSON } from "@terraformer/arcgis";
import store from "@/store";
import { createTerrainApi } from "@/services/terrain/api";
import { mapService } from "@/services/map/mapService";
import { transform } from "ol/proj";
import KML from "ol/format/KML.js";
import GeoJSON from "ol/format/GeoJSON.js";
import JSZip from "jszip";
import shpwrite from "@mapbox/shp-write";
import { formatDateValue, isZonedIsoDate } from "@/services/helpers/displayValueHelper";
import {
  activeAdvancedParts,
  arcgisCrossLayerRelationship,
  arcgisSpatialRelationship,
  clampToWebMercatorWorld,
  cloneFilter,
  compileAttributeTree,
  compileQuickFilters,
  describeAppliedFilter,
  emptyAppliedFilter,
  geoserverSpatialPredicate,
  hasAppliedPredicate,
  isConditionActive,
  migrateAppliedFilter,
  quickAppliedFilter,
  roiPolygon4326,
} from "./advancedFilters";
import {
  crossLayerCapabilities,
  crossLayerSignature,
  effectiveCrossLayerLimits,
  esriGeometryType,
  normalizeTargetGeometries,
  packAsEsriGeometry,
  repairCrossLayerGeometries,
} from "./crossLayerExecution";
import { filterImportedRecords } from "./importedFilters";
import { IMPORT_PROVIDER, importedLayerRegistry } from "./importedLayerRegistry";
import {
  effectiveAttributeFilter,
  isAttributeFilterEnabled,
  readSavedAttributeFilter,
  setAttributeFilterEnabled,
  writeSavedAttributeFilter,
} from "./attributeFilterState";

// Campos técnicos de Capa_Terreno (bookkeeping, no son respuestas del
// formulario) — se ocultan por defecto y van al final del orden de columnas
// la primera vez que se abre la tabla de atributos de una capa así, mientras
// el usuario no la haya personalizado ya (ver AttributesTable.vue). Solo
// aplica cuando el layer trae `provider.terrainFields` (marca de que es una
// Capa_Terreno — ver TerrainSection.vue).
// Exportados porque selectService.js (popup de feature del mapa) aplica el
// mismo criterio de campos técnicos/alias que la tabla de atributos.
export const TERRAIN_TECHNICAL_FIELDS = ["responseVersion", "terrainLayerId", "campaignId", "templateVersionId", "_id", "responseId", "updatedAt"];
const PROPERTIES_PUBLIC_PREFIX = "propertiesPublic.";
export const stripPropertiesPublicPrefix = name => (name.startsWith(PROPERTIES_PUBLIC_PREFIX) ? name.slice(PROPERTIES_PUBLIC_PREFIX.length) : name);

// Formatea únicamente la presentación. Los optionId crudos permanecen en
// el row para que filtros, descargas y consultas a GeoServer conserven su
// contrato estable.
export const formatAttributeValue = (metadata, field, value) => {
  if (value === null || value === undefined) return "";
  if (metadata && metadata.schema && metadata.schema[field] === "date" && (typeof value === "number" || isZonedIsoDate(value))) {
    return formatDateValue(value);
  }
  const labels = metadata && metadata.valueLabels && metadata.valueLabels[field];
  let values = value;
  if (!Array.isArray(values) && typeof values === "string" && values.trim().startsWith("[")) {
    try { values = JSON.parse(values); } catch (error) { /* GeoServer también puede entregar texto plano. */ }
  }
  if (Array.isArray(values)) return values.map(item => (labels && labels[String(item)]) || String(item)).join(", ");
  return (labels && labels[String(values)]) || String(values);
};

const RECORDS_PER_PAGE = 10;
const MAX_DOWNLOAD_RECORDS = 10000;
// Tope para el zoom "a resultados": recorrer todas las páginas de un filtro
// con miles de coincidencias antes de hacer zoom sería demasiado lento, así
// que el extent se calcula solo con las primeras MAX_ZOOM_EXTENT_RECORDS
// coincidencias (se avisa cuando el extent queda parcial).
const MAX_ZOOM_EXTENT_RECORDS = 2000;
// Tolerancia (metros, EPSG:3857) pedida a ArcGIS (maxAllowableOffset) al
// buscar la geometría de la capa objetivo de un filtro entre-capas — ver el
// comentario en prepareCrossLayer.
const TARGET_GEOMETRY_GENERALIZATION_METERS = 50;
// Umbral (cantidad de coordenadas) desde el cual una capa objetivo importada
// se considera "compleja" para efectos de la advertencia de UX del filtro
// entre-capas (ver estimateCrossLayerTargetComplexity) — no tiene relación
// con CROSS_LAYER_MAX_TARGET_COORDINATES (ese es un límite duro de rechazo,
// este es solo para decidir si mostrar el aviso de "puede demorar").
const COMPLEX_TARGET_COORDINATE_THRESHOLD = 50000;
const metadataCache = new Map();
const crossLayerArtifacts = new Map();

const normalizeType = (type = "") => {
  const value = type.toLowerCase();
  if (value.includes("gml:") || value.includes("geometry")) return "geometry";
  if (/(bool)/.test(value)) return "boolean";
  if (/(date|time)/.test(value)) return "date";
  if (/(int|long|short|double|float|decimal|number|oid)/.test(value)) return "number";
  if (/(string|text|char|guid)/.test(value)) return "text";
  return "unknown";
};

const inferType = value => {
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  return "text";
};

const checkResponse = async response => {
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  const data = await response.json();
  if (data && data.error) {
    const details = data.error.details && data.error.details.length ? `: ${data.error.details.join("; ")}` : "";
    throw new Error(`${data.error.message || "ArcGIS rechazó la consulta"}${details}`);
  }
  return data;
};

const postForm = (url, params, signal) => fetch(url, {
  method: "POST",
  headers: { "Content-Type": "application/x-www-form-urlencoded" },
  body: params.toString(),
  signal,
}).then(checkResponse);

const resolveArcgisUrls = layer => {
  const configured = String(layer.provider.url || "").replace(/\/+$/, "");
  if (/\/ImageServer(?:\/\d+)?$/i.test(configured)) throw new Error("ImageServer no admite filtros ROI de entidades");
  const concrete = configured.match(/\/(MapServer|FeatureServer)\/(\d+)$/i);
  if (concrete) {
    return {
      rootUrl: configured.replace(/\/\d+$/, ""),
      layerUrl: configured,
      serviceType: concrete[1].toLowerCase(),
      layerId: Number(concrete[2]),
    };
  }
  const root = configured.match(/\/(MapServer|FeatureServer)$/i);
  if (!root) throw new Error("URL ArcGIS no reconocida");
  const layerId = Number(layer.codigo == null || layer.codigo === "" ? 0 : layer.codigo);
  return { rootUrl: configured, layerUrl: `${configured}/${layerId}`, serviceType: root[1].toLowerCase(), layerId };
};

const ringSignedArea = ring => {
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    sum += x1 * y2 - x2 * y1;
  }
  return sum;
};

// El datastore MongoDB de GeoServer (Capa_Terreno) exige la regla de la mano
// derecha en cualquier Polygon del CQL_FILTER (anillo exterior antihorario,
// huecos horarios) y responde 400 ("Strict winding order is only supported
// by polygon") si no se cumple — a diferencia de PostGIS/ArcGIS, que no
// validan el orden de los anillos. Un ROI dibujado a mano (círculo,
// rectángulo, polígono libre) no siempre viene en ese orden — el círculo en
// particular siempre sale horario (el rumbo geodésico crece en sentido
// horario) — así que hay que forzarlo acá, en el único lugar donde se arma
// el WKT para cualquier capa GeoServer, Terreno o no.
const rightHandRuleRing = (ring, isHole) => {
  const isCounterClockwise = ringSignedArea(ring) > 0;
  return isCounterClockwise === !isHole ? ring : ring.slice().reverse();
};

const polygonToWkt = rings => `POLYGON(${rings.map((ring, index) => `(${rightHandRuleRing(ring, index > 0).map(coord => `${coord[0]} ${coord[1]}`).join(", ")})`).join(", ")})`;

// Exportado: AttributesTable.vue lo usa para mostrar el botón "Actualizar"
// solo en capas de Capa_Terreno (ver header-tools).
export const isTerrainLayer = layer => !!(layer && layer.provider && Array.isArray(layer.provider.terrainFields));

// combinedWkt (PB-24_v4) arma un WKT MULTIPOLYGON/MULTILINESTRING/MULTIPOINT
// a partir de artifact.geometries (lista de geometrías objetivo, ya
// reparadas por-feature pero SIN unir — ver prepareCrossLayer). Reemplaza
// al artifact.wkt único de antes: ya no existe una sola geometría fusionada
// de la que leer el WKT, así que se arma acá igual que polygonToWkt arma el
// de un ROI. Solo la familia Polygon necesita reforzar la regla de la mano
// derecha por anillo (rightHandRuleRing) — LineString/Point no tienen
// noción de winding.
const wktCoord = coord => `${coord[0]} ${coord[1]}`;

const combinedWkt = (geometries, family) => {
  if (family === "Polygon") {
    const parts = geometries.flatMap(g => (g.type === "MultiPolygon" ? g.coordinates : [g.coordinates]));
    return `MULTIPOLYGON(${parts.map(rings => `(${rings.map((ring, i) => `(${rightHandRuleRing(ring, i > 0).map(wktCoord).join(", ")})`).join(", ")})`).join(", ")})`;
  }
  if (family === "LineString") {
    const parts = geometries.flatMap(g => (g.type === "MultiLineString" ? g.coordinates : [g.coordinates]));
    return `MULTILINESTRING(${parts.map(line => `(${line.map(wktCoord).join(", ")})`).join(", ")})`;
  }
  if (family === "Point") {
    const points = geometries.flatMap(g => (g.type === "MultiPoint" ? g.coordinates : [g.coordinates]));
    return `MULTIPOINT(${points.map(p => `(${wktCoord(p)})`).join(", ")})`;
  }
  return null;
};

// crossLayerSpatialClause: fallback CQL_FILTER para capas GeoServer no
// respaldadas por Mongo/Terreno (esas van directo por el endpoint nativo
// de resolveGeoServerCrossLayerIds, nunca por acá — ver isTerrainLayer más
// abajo). No hace falta ningún tratamiento especial de huecos/partes: para
// esas capas GeoServer, un WKT con huecos/multi-parte es CQL válido normal
// (la restricción de gs-mongodb sobre huecos/multi-parte es específica del
// datastore Mongo de Terreno, que ya no pasa por acá).
const crossLayerSpatialClause = (predicate, geometryField, artifact) => {
  if (!artifact || artifact.empty || !artifact.geometries.length) return "EXCLUDE";
  const wkt = combinedWkt(artifact.geometries, artifact.geometryFamily);
  if (!wkt) return "EXCLUDE";
  return `${predicate}(${geometryField}, SRID=3857;${wkt})`;
};

const roiRings3857 = spec => (roiPolygon4326(spec) || []).map(ring => ring.map(coord => transform(coord, "EPSG:4326", "EPSG:3857")));

// reproject3857To4326Coordinates recorre recursivamente el árbol de
// coordenadas de un GeoJSON Polygon/MultiPolygon (2 o 3 niveles de
// anidamiento respectivamente) reproyectando cada punto de EPSG:3857 a
// EPSG:4326. MongoDB exige grados WGS84 para $geoIntersects/$geoWithin
// (PB-24_v3), mientras que la geometría objetivo del filtro entre-capas
// llega de geoprocess ya en EPSG:3857 (mismo CRS que el resto del visor).
const reproject3857To4326Coordinates = node => (
  typeof node[0] === "number" ? transform(node, "EPSG:3857", "EPSG:4326") : node.map(reproject3857To4326Coordinates)
);

const terrainGeoIntersectGeometries = artifact => artifact.geometries.map(geometry => ({
  type: geometry.type,
  coordinates: reproject3857To4326Coordinates(geometry.coordinates),
}));

const terrainGeoIntersectOperation = crossLayerOperation => (
  geoserverSpatialPredicate(crossLayerOperation) === "WITHIN" ? "within" : "intersects"
);

const arcgisGeometry = spec => ({
  rings: roiRings3857(spec),
  spatialReference: { wkid: 3857 },
});

const featureExtent = geometry => {
  if (!geometry) return null;
  if (geometry.x !== undefined && geometry.y !== undefined) return [geometry.x - 100, geometry.y - 100, geometry.x + 100, geometry.y + 100];
  const collections = geometry.rings || geometry.paths || (geometry.points ? [geometry.points] : []);
  let xmin = Infinity; let ymin = Infinity; let xmax = -Infinity; let ymax = -Infinity;
  collections.forEach(part => part.forEach(coord => {
    xmin = Math.min(xmin, coord[0]); ymin = Math.min(ymin, coord[1]);
    xmax = Math.max(xmax, coord[0]); ymax = Math.max(ymax, coord[1]);
  }));
  return Number.isFinite(xmin) ? [xmin, ymin, xmax, ymax] : null;
};

// featureExtent (arriba) solo entiende geometría Esri (rings/paths/points/x-y)
// — las respuestas WFS/GeoServer (fetchGeoServer) traen GeoJSON estándar en
// vez de eso. GeoServer tampoco siempre incluye `bbox` por feature en la
// respuesta (depende de versión/config del datastore — Capa_Terreno, por
// ejemplo, nunca lo trae), así que no se puede confiar en `feature.bbox`
// como sí hacían los otros proveedores; hay que calcularlo. Por eso "zoom a
// resultados" siempre tiraba "Los resultados no tienen ubicación disponible"
// con capas GeoServer: computeAggregateBbox (bboxHelper.js) recibía
// bbox:null en cada fila.
export const geoJsonExtent = geometry => {
  if (!geometry) return null;
  try {
    return new GeoJSON().readGeometry(geometry).getExtent();
  } catch (error) {
    return null;
  }
};

// esriGeometryToGeoJSON delega en @terraformer/arcgis (Esri, mismo motor que
// usa su propio JS API) en vez de una conversión artesanal. La versión
// anterior (`{type: "Polygon", coordinates: geometry.rings}`) trataba TODO
// anillo después del primero como hueco, sin importar si era un hueco real
// o una parte disjunta (ej. una isla) — para una capa objetivo con partes
// múltiples (regiones administrativas con archipiélagos) eso produce
// topología silenciosamente incorrecta. arcgisToGeoJSON agrupa los anillos
// correctamente (signo de área + contención), devolviendo un MultiPolygon
// real cuando corresponde — verificado con datos sintéticos y con una
// región real (Magallanes) antes de adoptarlo.
export const esriGeometryToGeoJSON = geometry => (geometry ? arcgisToGeoJSON(geometry) : null);

const allKnownLayers = () => {
  const catalog = (store.getters.getLayers || []).reduce((items, category) => items.concat(category.layers || []), []);
  return [...(store.getters.getActiveLayers || []), ...(store.getters.getImportedLayers || []), ...catalog]
    .filter((layer, index, layers) => layer && layers.findIndex(item => item.id === layer.id) === index);
};

const findKnownLayer = id => allKnownLayers().find(layer => String(layer.id) === String(id)) || null;

const countGeometryCoordinates = geometry => {
  if (!geometry || !geometry.coordinates) return 0;
  let count = 0;
  const walk = value => {
    if (typeof value[0] === "number") { count += 1; return; }
    value.forEach(walk);
  };
  walk(geometry.coordinates);
  return count;
};

// estimateCrossLayerTargetComplexity (PB-24_v4 UX): a diferencia de una capa
// GeoServer/ArcGIS (cuya geometría objetivo recién se conoce después de
// pedirla — ver prepareCrossLayer), una capa importada ya está completa en
// memoria (importedLayerRegistry, cargada al subir el archivo) — se puede
// contar sus coordenadas reales sin ningún costo de red antes de aplicar el
// filtro. Devuelve null cuando no se puede saber de antemano (cualquier otro
// proveedor), para que el llamador use un fallback basado en tiempo en vez
// de asumir "liviana" por defecto.
const estimateCrossLayerTargetComplexity = targetLayerId => {
  const targetLayer = findKnownLayer(targetLayerId);
  if (!targetLayer || targetLayer.provider.name !== IMPORT_PROVIDER) return null;
  const entry = importedLayerRegistry.get(targetLayer.id);
  if (!entry) return null;
  const coordinateCount = entry.records.reduce((sum, record) => sum + countGeometryCoordinates(record.row.geometry), 0);
  return { coordinateCount, complex: coordinateCount > COMPLEX_TARGET_COORDINATE_THRESHOLD };
};

const targetAppliedFilter = (crossLayer, roi) => ({
  version: 2,
  mode: "advanced",
  advanced: {
    attribute: crossLayer.targetAttribute || { enabled: false, root: null },
    roi: { enabled: !!(crossLayer.applyRoiToTarget && roi), spec: crossLayer.applyRoiToTarget ? roi : null },
    crossLayer: { enabled: false },
  },
});

const GEOMETRY_TYPE_FAMILY = {
  Polygon: "Polygon", MultiPolygon: "Polygon",
  LineString: "LineString", MultiLineString: "LineString",
  Point: "Point", MultiPoint: "Point",
};

// gmlGeometryFamily/esriGeometryFamily: derivan la MISMA familia
// (GEOMETRY_TYPE_FAMILY, ya usada para los artifacts del filtro entre-capas)
// a partir del tipo geométrico crudo que informa cada proveedor — GeoServer
// vía el atributo "type" del elemento geométrico en DescribeFeatureType
// (p.ej. "gml:MultiPolygonPropertyType" / "gml:MultiSurfacePropertyType"),
// ArcGIS vía metadata.geometryType ("esriGeometryPolygon"). Sirve para saber
// si una capa objetivo del filtro entre-capas es de superficie (selección
// por clic, ver AdvancedFilterPanel.vue) sin adivinar por el nombre de capa.
const gmlGeometryFamily = (type = "") => {
  const value = type.toLowerCase();
  if (/(polygon|surface)/.test(value)) return "Polygon";
  if (/(curve|line)/.test(value)) return "LineString";
  if (/point/.test(value)) return "Point";
  return null;
};

const esriGeometryFamily = (type = "") => ({
  esriGeometryPolygon: "Polygon",
  esriGeometryPolyline: "LineString",
  esriGeometryPoint: "Point",
  esriGeometryMultipoint: "Point",
}[type] || null);

// repairCrossLayerTarget/normalizeCrossLayerTarget (PB-24_v4) producen el
// mismo contrato uniforme — { empty, geometries, geometryFamily, stats } —
// sea cual sea el camino de geoprocess elegido en prepareCrossLayer:
// repair_features() ya devuelve una lista; normalize() devuelve una sola
// geometría fusionada, que se envuelve en un array de 1 elemento. Así
// resolveGeoServerCrossLayerIds/resolveArcgisSourceIds/matchesCrossLayer/
// crossLayerSpatialClause siempre operan sobre artifact.geometries sin
// necesitar saber cuál camino la produjo.
const repairCrossLayerTarget = async (geometries, signal) => {
  const result = await repairCrossLayerGeometries(geometries, signal);
  return {
    empty: !result.features.length,
    geometries: result.features.map(feature => feature.geometry),
    geometryFamily: result.geometryFamily,
    stats: result.stats,
  };
};

const normalizeCrossLayerTarget = async (geometries, signal) => {
  const result = await normalizeTargetGeometries(geometries, signal);
  return {
    empty: result.empty,
    geometries: result.empty ? [] : [result.geometry],
    geometryFamily: result.empty ? null : (GEOMETRY_TYPE_FAMILY[result.geometryType] || null),
    stats: result.stats,
  };
};

export const attributesTableService = {
  resolveArcgisUrls,
  formatAttributeValue,

  async getLayerMetadata(layer, force = false) {
    const cacheKey = `${layer.provider.name}:${layer.id}`;
    if (!force && metadataCache.has(cacheKey)) return metadataCache.get(cacheKey);
    let metadata;
    if (layer.provider.name === IMPORT_PROVIDER) {
      const entry = importedLayerRegistry.get(layer.id);
      if (!entry) throw new Error("Active la capa importada para consultar sus atributos");
      if (entry.metadata.geometryFamily === undefined) {
        const sample = entry.records.find(record => record.row.geometry && record.row.geometry.type);
        entry.metadata.geometryFamily = sample ? (GEOMETRY_TYPE_FAMILY[sample.row.geometry.type] || null) : null;
      }
      metadata = entry.metadata;
    } else {
      metadata = layer.provider.name === "geoserver"
        ? await this.getGeoServerMetadata(layer)
        : await this.getArcgisMetadata(layer);
    }
    metadataCache.set(cacheKey, metadata);
    return metadata;
  },

  async getGeoServerMetadata(layer) {
    const providerData = layer.provider.geoserverdata;
    const typeName = `${providerData.workspace}:${providerData.datastore}`;
    const baseUrl = String(layer.provider.url).replace(/\/+$/, "");
    const describeUrl = `${baseUrl}/wfs?service=WFS&version=2.0.0&request=DescribeFeatureType&typeNames=${encodeURIComponent(typeName)}`;
    const terrainFields = Array.isArray(layer.provider.terrainFields) ? layer.provider.terrainFields : null;
    const terrainFieldByID = new Map((terrainFields || []).map(field => [field.fieldId, field]));
    const terrainLabels = new Map((terrainFields || []).map(field => [field.fieldId, field.label]));
    const schema = {}; const aliases = {}; const valueLabels = {}; let geometryField = null; let geometryFamily = null;
    const registerValueLabels = (fieldName, field) => {
      if (!field || !Array.isArray(field.options) || !field.options.length) return;
      valueLabels[fieldName] = Object.fromEntries(field.options.map(option => [option.optionId, option.label]));
    };

    // GeoServer solo provisiona el feature type de una Capa_Terreno con la
    // primera respuesta materializada (ver Upsert en
    // repository/terrain/feature_geoserver_repo.go) — una campaña sin
    // respuestas aprobadas todavía NO tiene feature type, y DescribeFeatureType
    // responde con un ServiceExceptionReport (o un 4xx según la versión) en
    // vez del schema esperado. No es un error real, así que se trata aparte
    // de cualquier otra falla (red caída, servidor abajo, etc.).
    const response = await fetch(describeUrl);
    if (response.ok) {
      const xml = new DOMParser().parseFromString(await response.text(), "text/xml");
      Array.from(xml.getElementsByTagNameNS("*", "element")).forEach(element => {
        const name = element.getAttribute("name");
        const rawType = element.getAttribute("type") || "";
        const normalized = normalizeType(rawType);
        if (!name) return;
        if (normalized === "geometry" && !geometryField) { geometryField = name; geometryFamily = gmlGeometryFamily(rawType); }
        else if (normalized !== "geometry") {
          schema[name] = normalized;
          // terrainLabels está indexado por fieldId "pelado" (sin el prefijo
          // que agrega el datastore Mongo de GeoServer, p.ej.
          // "propertiesPublic.campo_x" -> "campo_x") - buscar con `name` tal
          // cual siempre fallaba acá y caía al fallback (el propio fieldId,
          // no el label real), que es justo lo que se veía en tabla/popup
          // apenas la Capa_Terreno tenía su primera respuesta aprobada.
          const strippedName = stripPropertiesPublicPrefix(name);
          aliases[name] = terrainFields ? (terrainLabels.get(strippedName) || strippedName) : name;
          registerValueLabels(name, terrainFieldByID.get(strippedName));
        }
      });
    }

    if (!geometryField) {
      if (!terrainFields) throw new Error(response.ok ? "No se encontró el campo geométrico de la capa" : response.statusText);
      // Capa_Terreno sin datos aún: se arma un esquema vacío desde
      // terrainFields (para que la tabla muestre las columnas con su label
      // real igual que si tuviera datos) y se marca `empty` para que
      // openTable() no intente consultar features de un feature type que no
      // existe.
      terrainFields.forEach(field => {
        schema[field.fieldId] = "string";
        aliases[field.fieldId] = field.label;
        registerValueLabels(field.fieldId, field);
      });
      return { provider: "geoserver", typeName, baseUrl, schema, aliases, valueLabels, geometryField: null, geometryFamily: null, crs: "EPSG:3857", defaultHiddenFields: [], empty: true };
    }

    const defaultHiddenFields = terrainFields ? TERRAIN_TECHNICAL_FIELDS.filter(field => schema[field]) : [];
    return { provider: "geoserver", typeName, baseUrl, schema, aliases, valueLabels, geometryField, geometryFamily, crs: "EPSG:3857", defaultHiddenFields };
  },

  async getArcgisMetadata(layer) {
    const urls = resolveArcgisUrls(layer);
    const [layerMetadata, rootMetadata] = await Promise.all([
      fetch(`${urls.layerUrl}?f=pjson`).then(checkResponse),
      fetch(`${urls.rootUrl}?f=pjson`).then(checkResponse),
    ]);
    const schema = {}; const aliases = {};
    (layerMetadata.fields || []).forEach(field => {
      schema[field.name] = normalizeType(field.type);
      aliases[field.name] = field.alias || field.name;
    });
    const objectIdField = layerMetadata.objectIdField || layerMetadata.objectIdFieldName ||
      (layerMetadata.fields || []).find(field => /oid/i.test(field.type || ""))?.name;
    return {
      provider: "arcgis",
      ...urls,
      schema,
      aliases,
      objectIdField,
      geometryType: layerMetadata.geometryType,
      geometryFamily: esriGeometryFamily(layerMetadata.geometryType),
      supportsPagination: layerMetadata.advancedQueryCapabilities
        ? layerMetadata.advancedQueryCapabilities.supportsPagination !== false : true,
      supportedSpatialRelationships: layerMetadata.supportedSpatialRelationships || null,
      supportsSpatialFilter: urls.serviceType === "mapserver" && rootMetadata.supportsSpatialFilter === true,
    };
  },

  findKnownLayer,
  estimateCrossLayerTargetComplexity,

  // fetchTargetFeaturesByIds: contraparte de "por atributos" para la capa
  // objetivo del filtro entre-capas cuando targetSelectionMode === "click"
  // (AdvancedFilterPanel.vue) — en vez de compilar un árbol de condiciones,
  // resuelve directamente las geometrías de los ids elegidos a mano en el
  // mapa. Reutiliza el MISMO patrón de fetch-por-id-nativo ya probado en
  // fetchGeoServer/fetchArcgis (rama crossLayer de paginación): CQL "IN
  // (fid,...)" para GeoServer, "objectIds" para ArcGIS. Para importadas no
  // hace falta red: el id es el índice en entry.records. Devuelve pares
  // {id, geometry} (no una lista plana) porque targetFeatureSelectionService
  // necesita reasociar cada geometría a SU id para el resaltado — el servidor
  // no garantiza devolver los features en el mismo orden en que se pidieron.
  async fetchTargetFeaturesByIds(targetLayer, metadata, ids, signal) {
    if (!ids.length) return [];
    if (metadata.provider === IMPORT_PROVIDER) {
      const entry = importedLayerRegistry.get(targetLayer.id);
      if (!entry) throw new Error("La capa importada ya no está disponible");
      return ids
        .map(id => ({ id, record: entry.records[id] }))
        .filter(({ record }) => record && record.row.geometry)
        .map(({ id, record }) => ({ id, geometry: record.row.geometry }));
    }
    const results = [];
    for (let offset = 0; offset < ids.length; offset += 250) {
      const pageIds = ids.slice(offset, offset + 250);
      if (metadata.provider === "geoserver") {
        const params = new URLSearchParams({
          service: "WFS", version: "2.0.0", request: "GetFeature", typeNames: metadata.typeName,
          outputFormat: "application/json", srsName: "EPSG:3857",
          CQL_FILTER: `IN (${pageIds.map(id => `'${id}'`).join(", ")})`,
        });
        const response = await postForm(`${metadata.baseUrl}/wfs`, params, signal);
        (response.features || []).forEach(feature => {
          if (feature.geometry && feature.id != null) results.push({ id: feature.id, geometry: feature.geometry });
        });
      } else {
        const params = new URLSearchParams({ f: "json", objectIds: pageIds.join(","), outFields: "", returnGeometry: "true", outSR: "3857" });
        const data = await postForm(`${metadata.layerUrl}/query`, params, signal);
        (data.features || []).forEach(feature => {
          const geometry = esriGeometryToGeoJSON(feature.geometry);
          const id = feature.attributes && (feature.attributes[metadata.objectIdField] ?? feature.attributes.OBJECTID);
          if (geometry && id != null) results.push({ id, geometry });
        });
      }
    }
    return results;
  },

  async prepareCrossLayer(layer, appliedFilter, signal) {
    const parts = activeAdvancedParts(appliedFilter);
    if (!parts.crossLayer) return null;
    const signature = crossLayerSignature(layer.id, appliedFilter);
    if (crossLayerArtifacts.has(signature)) return crossLayerArtifacts.get(signature);

    const targetLayer = findKnownLayer(parts.crossLayer.targetLayerId);
    if (!targetLayer) throw new Error("La capa objetivo ya no está disponible. El filtro cruzado se conservó, pero no puede aplicarse.");
    const capabilities = await crossLayerCapabilities();
    const limits = effectiveCrossLayerLimits(capabilities);
    const targetMetadata = await this.getLayerMetadata(targetLayer);
    let geometries;
    if (parts.crossLayer.targetSelectionMode === "click") {
      const ids = (parts.crossLayer.targetSelection && parts.crossLayer.targetSelection.ids) || [];
      if (ids.length > limits.maxTargetFeatures) {
        throw new Error(`Seleccionó ${ids.length} entidades (máximo ${limits.maxTargetFeatures}).`);
      }
      geometries = (await this.fetchTargetFeaturesByIds(targetLayer, targetMetadata, ids, signal)).map(item => item.geometry);
    } else {
      const targetFilter = targetAppliedFilter(parts.crossLayer, parts.roi);
      geometries = [];
      let offset = 0;
      let total = 0;
      do {
        const result = await this.fetchAttributes(targetLayer, {
          offset,
          count: Math.min(250, limits.maxTargetFeatures + 1 - geometries.length),
          appliedFilter: targetFilter,
          metadata: targetMetadata,
          signal,
          // La geometría objetivo se va a simplificar de nuevo en geoprocess
          // (SIMPLIFY_TOLERANCE_METERS = 50m, cross_layer_service.py) antes de
          // usarse para cualquier filtro — no hace falta pedirle a la capa
          // origen su geometría a resolución completa. Para una capa ArcGIS
          // (ej. límites administrativos con costas/archipiélagos muy
          // detallados) pedir la geometría completa puede ser en sí mismo lo
          // lento, sin importar nada de lo que hagamos después con ella;
          // generalizarla en el propio servidor ArcGIS (maxAllowableOffset)
          // reduce el tamaño de la respuesta y el tiempo de armado en origen.
          // Mismo valor que geoprocess para no mantener dos tolerancias
          // encadenadas por separado.
          maxAllowableOffset: TARGET_GEOMETRY_GENERALIZATION_METERS,
        });
        total = result.total;
        if (total > limits.maxTargetFeatures) {
          throw new Error(`La capa objetivo produce ${total} entidades. Agregue una región de interés o condiciones (máximo ${limits.maxTargetFeatures}).`);
        }
        geometries.push(...result.features.map(feature => feature.geometry).filter(Boolean));
        offset += result.features.length;
        if (!result.features.length) break;
      } while (offset < total && geometries.length <= limits.maxTargetFeatures);
    }

    // PB-24_v4: no unir (repair_features(), rápido) cuando es seguro no
    // hacerlo — Terreno siempre es origen Point, o la operación es
    // "intersects" (segura sin unir sin importar el origen: cualquier parte
    // sin fusionar que intersecte alcanza, a diferencia de "dentro-de"/
    // "contiene", que si el origen cruza justo el borde compartido entre
    // dos partes vecinas del target sin unir podría dar un falso negativo).
    // Unir (normalize(), lento) solo para ese caso angosto restante —
    // decisión explícita tomada con el trade-off ya medido, no un default
    // silencioso (ver thoughts/shared/plans/2026-07-27_PB-24_v4 y el plan
    // aprobado en C:\Users\aleje\.claude\plans\).
    const canSkipUnion = isTerrainLayer(layer) || parts.crossLayer.operation === "intersects";
    const artifact = canSkipUnion
      ? await repairCrossLayerTarget(geometries, signal)
      : await normalizeCrossLayerTarget(geometries, signal);
    Object.assign(artifact, { signature, targetLayerId: targetLayer.id, limits });
    crossLayerArtifacts.set(signature, artifact);
    return artifact;
  },

  getCrossLayerArtifact(layer, appliedFilter) {
    return crossLayerArtifacts.get(crossLayerSignature(layer.id, appliedFilter)) || null;
  },

  invalidateCrossLayer(layer, appliedFilter) {
    if (activeAdvancedParts(appliedFilter).crossLayer) {
      crossLayerArtifacts.delete(crossLayerSignature(layer.id, appliedFilter));
    }
  },

  async resolveArcgisSourceIds(layer, appliedFilter, metadata, artifact, signal) {
    if (artifact.arcgisSourceIds) return artifact.arcgisSourceIds;
    if (artifact.empty) { artifact.arcgisSourceIds = []; return []; }
    if (!metadata.objectIdField) throw new Error("La capa ArcGIS principal no informa un campo ObjectID");
    const parts = activeAdvancedParts(appliedFilter);
    const compiled = this.buildProviderFilter(appliedFilter, metadata);
    const crossRelationship = arcgisCrossLayerRelationship(parts.crossLayer.operation);
    if (Array.isArray(metadata.supportedSpatialRelationships) && !metadata.supportedSpatialRelationships.includes(crossRelationship)) {
      throw new Error("La relación espacial seleccionada no es soportada por la capa ArcGIS principal");
    }
    const queryIds = async (geometry, geometryType, spatialRel) => {
      const params = new URLSearchParams({
        f: "json",
        where: compiled.attribute || "1=1",
        returnGeometry: "false",
        returnIdsOnly: "true",
        geometry: JSON.stringify(geometry),
        geometryType,
        inSR: "3857",
        spatialRel,
      });
      const data = await postForm(`${metadata.layerUrl}/query`, params, signal);
      return data.objectIds || [];
    };
    const crossIds = await queryIds(
      packAsEsriGeometry(artifact.geometries, artifact.geometryFamily),
      esriGeometryType(artifact.geometryFamily),
      crossRelationship
    );
    let ids = crossIds;
    if (parts.roi) {
      const roiIds = await queryIds(arcgisGeometry(parts.roi), "esriGeometryPolygon", arcgisSpatialRelationship(parts.roi.operation));
      const roiSet = new Set(roiIds.map(String));
      ids = crossIds.filter(id => roiSet.has(String(id)));
    }
    if (ids.length > artifact.limits.maxArcgisSourceIds) {
      throw new Error(`El filtro devuelve ${ids.length} entidades ArcGIS. Acote la consulta a un máximo de ${artifact.limits.maxArcgisSourceIds}.`);
    }
    artifact.arcgisSourceIds = ids;
    return ids;
  },

  // resolveGeoServerCrossLayerIds: mismo patrón que resolveArcgisSourceIds
  // (resolver una vez, cachear en el artifact, paginar por ID) — sin esto,
  // fetchGeoServer reenviaba el CQL_FILTER espacial completo (atributo+ROI+
  // cruce de capas) en cada página/re-render. Para un objetivo con muchas
  // partes (ej. una región con archipiélagos → cientos de cláusulas OR) esa
  // consulta es cara de parsear/ejecutar; repetirla en cada interacción del
  // usuario (paginar, reabrir la tabla, etc.) es lo que se sentía lento,
  // comparado con capas importadas (que filtran en memoria, sin red). Acá la
  // consulta espacial se corre una sola vez por filtro aplicado; el resto de
  // interacciones solo hacen `IN (id1, id2, ...)`, una consulta trivial.
  async resolveGeoServerCrossLayerIds(layer, appliedFilter, metadata, artifact, signal, mapId) {
    if (!artifact) return [];
    if (artifact.geoserverCrossLayerIds) return artifact.geoserverCrossLayerIds;
    if (artifact.empty) { artifact.geoserverCrossLayerIds = []; return []; }
    const crossLayer = activeAdvancedParts(appliedFilter).crossLayer;
    // Capa_Terreno resuelve el predicado espacial directo contra MongoDB
    // (PB-24_v3), no vía CQL_FILTER — el traductor CQL->Mongo de gs-mongodb
    // tiene restricciones reales (multi-parte, huecos en polígonos
    // "grandes") que no existen en Mongo nativo. artifact.geometries ya
    // viene sin unir cuando esto corre (prepareCrossLayer siempre usa el
    // camino rápido para Terreno, ver ahí) — se empaquetan las partes en un
    // solo MultiPolygon del lado del backend Go (PackGeometriesAsMultiPolygon),
    // no acá.
    if (isTerrainLayer(layer)) {
      const terrainApi = createTerrainApi(axios, store.getters.backendurl);
      const response = await terrainApi.resolveGeoIntersectIds(
        mapId,
        layer.terrainLayerId,
        terrainGeoIntersectGeometries(artifact),
        terrainGeoIntersectOperation(crossLayer.operation),
      );
      artifact.geoserverCrossLayerIds = response.data.featureIds || [];
      return artifact.geoserverCrossLayerIds;
    }
    const compiled = this.buildProviderFilter(appliedFilter, metadata);
    const clauses = [];
    if (compiled.attribute) clauses.push(compiled.attribute);
    if (compiled.roi) {
      const rings = roiRings3857(compiled.roi);
      const predicate = geoserverSpatialPredicate(compiled.roi.operation);
      clauses.push(`${predicate}(${metadata.geometryField}, SRID=3857;${polygonToWkt(rings)})`);
    }
    clauses.push(crossLayerSpatialClause(geoserverSpatialPredicate(crossLayer.operation), metadata.geometryField, artifact));
    const params = new URLSearchParams({
      service: "WFS", version: "2.0.0", request: "GetFeature", typeNames: metadata.typeName,
      outputFormat: "application/json", srsName: "EPSG:3857",
    });
    params.set("CQL_FILTER", clauses.map(clause => `(${clause})`).join(" AND "));
    const response = await postForm(`${metadata.baseUrl}/wfs`, params, signal);
    const ids = (response.features || []).map(feature => feature.id).filter(Boolean);
    artifact.geoserverCrossLayerIds = ids;
    return ids;
  },

  buildProviderFilter(appliedFilter, metadata) {
    const filter = appliedFilter || emptyAppliedFilter();
    let attribute = null; let roi = null;
    if (filter.mode === "quick") attribute = compileQuickFilters(filter.quick, metadata.provider, metadata.schema);
    if (filter.mode === "advanced") {
      const parts = activeAdvancedParts(filter);
      attribute = parts.attribute ? compileAttributeTree(parts.attribute, metadata.provider, metadata.schema) : null;
      roi = parts.roi;
    }
    if (roi && metadata.provider === "arcgis" && Array.isArray(metadata.supportedSpatialRelationships)) {
      const relationship = arcgisSpatialRelationship(roi.operation);
      if (!metadata.supportedSpatialRelationships.includes(relationship)) {
        throw new Error("La relación espacial seleccionada no es soportada por esta capa ArcGIS");
      }
    }
    return { attribute, roi };
  },

  async fetchAttributes(layer, options = {}) {
    const metadata = options.metadata || await this.getLayerMetadata(layer);
    const request = {
      offset: options.offset || 0,
      count: options.count || RECORDS_PER_PAGE,
      appliedFilter: options.appliedFilter || emptyAppliedFilter(),
      metadata,
      signal: options.signal,
      mapId: options.mapId,
      maxAllowableOffset: options.maxAllowableOffset,
    };
    request.crossLayerArtifact = await this.prepareCrossLayer(layer, request.appliedFilter, request.signal);
    if (metadata.provider === IMPORT_PROVIDER) return this.fetchImported(layer, request);
    return metadata.provider === "geoserver" ? this.fetchGeoServer(layer, request) : this.fetchArcgis(layer, request);
  },

  async fetchImported(layer, { offset, count, appliedFilter, metadata, crossLayerArtifact }) {
    const entry = importedLayerRegistry.get(layer.id);
    if (!entry) throw new Error("La capa importada ya no está disponible");
    const filtered = filterImportedRecords(entry.records, appliedFilter, metadata.schema, crossLayerArtifact);
    return {
      features: filtered.slice(offset, offset + count).map(record => record.row),
      total: filtered.length,
    };
  },

  // fetchTotalCount: cantidad TOTAL de registros de la capa, sin importar el
  // filtro aplicado — para el resumen "X de Y registros" de AttributesTable.vue.
  // Independiente de fetchAttributes: acá no interesa la geometría ni las
  // columnas, solo el conteo, así que se pide lo más liviano que soporta cada
  // proveedor (WFS resultType=hits / ArcGIS returnCountOnly, sin CQL_FILTER
  // ni where más que "1=1").
  async fetchTotalCount(layer, metadata, signal) {
    if (metadata.provider === IMPORT_PROVIDER) {
      const entry = importedLayerRegistry.get(layer.id);
      return entry ? entry.records.length : 0;
    }
    if (metadata.provider === "geoserver") {
      // resultType=hits devuelve un ExceptionReport XML en este GeoServer
      // (no soporta hits + outputFormat=json) — se usa el mismo tipo de
      // pedido que ya funciona para la página filtrada (fetchGeoServer),
      // sin CQL_FILTER y con count=1 para no traer más que una fila.
      const params = new URLSearchParams({
        service: "WFS", version: "2.0.0", request: "GetFeature", typeNames: metadata.typeName,
        outputFormat: "application/json", srsName: "EPSG:3857", count: "1", startIndex: "0",
      });
      const response = await postForm(`${metadata.baseUrl}/wfs`, params, signal);
      const matched = response.numberMatched !== undefined ? response.numberMatched : response.totalFeatures;
      return Number.isFinite(Number(matched)) ? Number(matched) : 0;
    }
    const queryUrl = `${metadata.layerUrl}/query`;
    if (metadata.supportsPagination) {
      const params = new URLSearchParams({ f: "json", where: "1=1", returnGeometry: "false", returnCountOnly: "true" });
      const data = await postForm(queryUrl, params, signal);
      return Number(data.count) || 0;
    }
    const params = new URLSearchParams({ f: "json", where: "1=1", returnGeometry: "false", returnIdsOnly: "true" });
    const data = await postForm(queryUrl, params, signal);
    return (data.objectIds || []).length;
  },

  // No se espera (fire-and-forget): el conteo total es un dato secundario del
  // resumen de la tabla, no debe demorar la apertura ni el refresh. Se
  // descarta si para cuando resuelve el usuario ya cambió de capa.
  refreshTotalUnfiltered(layer, metadata) {
    this.fetchTotalCount(layer, metadata).then(total => {
      const current = store.getters["attributeTable/layer"];
      if (current && current.id === layer.id) store.dispatch("attributeTable/setTotalUnfiltered", total);
    }).catch(error => console.warn("[attributesTable] fetchTotalCount:", error));
  },

  syncImportedMapFilter(layer, appliedFilter, metadata) {
    const entry = importedLayerRegistry.get(layer.id);
    if (!entry) return;
    const filtered = filterImportedRecords(entry.records, appliedFilter, metadata.schema, this.getCrossLayerArtifact(layer, appliedFilter));
    importedLayerRegistry.setVisibleRecords(layer.id, filtered);
  },

  async fetchGeoServer(layer, { offset, count, appliedFilter, metadata, signal, crossLayerArtifact, mapId }) {
    const parseFeatures = response => (response.features || []).map(feature => ({
      ...(feature.properties || {}), geometry: feature.geometry || null, bbox: feature.bbox || geoJsonExtent(feature.geometry),
    }));
    const crossLayer = activeAdvancedParts(appliedFilter).crossLayer;
    if (crossLayer) {
      if (crossLayerArtifact && crossLayerArtifact.empty) return { features: [], total: 0 };
      const ids = await this.resolveGeoServerCrossLayerIds(layer, appliedFilter, metadata, crossLayerArtifact, signal, mapId);
      const pageIds = ids.slice(offset, offset + count);
      if (!pageIds.length) return { features: [], total: ids.length };
      const params = new URLSearchParams({
        service: "WFS", version: "2.0.0", request: "GetFeature", typeNames: metadata.typeName,
        outputFormat: "application/json", srsName: "EPSG:3857",
        CQL_FILTER: `IN (${pageIds.map(id => `'${id}'`).join(", ")})`,
      });
      const response = await postForm(`${metadata.baseUrl}/wfs`, params, signal);
      return { features: parseFeatures(response), total: ids.length };
    }
    const compiled = this.buildProviderFilter(appliedFilter, metadata);
    const clauses = [];
    if (compiled.attribute) clauses.push(compiled.attribute);
    if (compiled.roi) {
      const rings = roiRings3857(compiled.roi);
      const predicate = geoserverSpatialPredicate(compiled.roi.operation);
      clauses.push(`${predicate}(${metadata.geometryField}, SRID=3857;${polygonToWkt(rings)})`);
    }
    const params = new URLSearchParams({
      service: "WFS", version: "2.0.0", request: "GetFeature", typeNames: metadata.typeName,
      outputFormat: "application/json", srsName: "EPSG:3857", count: String(count), startIndex: String(offset),
    });
    if (clauses.length) params.set("CQL_FILTER", clauses.map(clause => `(${clause})`).join(" AND "));
    const response = await postForm(`${metadata.baseUrl}/wfs`, params, signal);
    const matched = response.numberMatched !== undefined ? response.numberMatched : response.totalFeatures;
    return { features: parseFeatures(response), total: Number.isFinite(Number(matched)) ? Number(matched) : offset + parseFeatures(response).length };
  },

  arcgisBaseParams(appliedFilter, metadata) {
    const compiled = this.buildProviderFilter(appliedFilter, metadata);
    const params = new URLSearchParams({
      f: "json", where: compiled.attribute || "1=1", outFields: "*", outSR: "3857",
    });
    if (compiled.roi) {
      params.set("geometry", JSON.stringify(arcgisGeometry(compiled.roi)));
      params.set("geometryType", "esriGeometryPolygon");
      params.set("inSR", "3857");
      params.set("spatialRel", arcgisSpatialRelationship(compiled.roi.operation));
    }
    return params;
  },

  async fetchArcgis(layer, { offset, count, appliedFilter, metadata, signal, crossLayerArtifact, maxAllowableOffset }) {
    const queryUrl = `${metadata.layerUrl}/query`;
    const crossLayer = activeAdvancedParts(appliedFilter).crossLayer;
    if (crossLayer) {
      const ids = await this.resolveArcgisSourceIds(layer, appliedFilter, metadata, crossLayerArtifact, signal);
      const pageIds = ids.slice(offset, offset + count);
      if (!pageIds.length) return { features: [], total: ids.length };
      const pageParams = new URLSearchParams({
        f: "json",
        objectIds: pageIds.join(","),
        outFields: "*",
        returnGeometry: "true",
        outSR: "3857",
      });
      const pageData = await postForm(queryUrl, pageParams, signal);
      const features = (pageData.features || []).map(feature => ({
        ...(feature.attributes || {}),
        geometry: esriGeometryToGeoJSON(feature.geometry),
        bbox: featureExtent(feature.geometry),
      }));
      return { features, total: ids.length };
    }
    const base = this.arcgisBaseParams(appliedFilter, metadata);
    let pageData; let total;
    if (metadata.supportsPagination) {
      const pageParams = new URLSearchParams(base);
      pageParams.set("returnGeometry", "true");
      pageParams.set("resultOffset", String(offset));
      pageParams.set("resultRecordCount", String(count));
      if (maxAllowableOffset) pageParams.set("maxAllowableOffset", String(maxAllowableOffset));
      const countParams = new URLSearchParams(base);
      countParams.set("returnGeometry", "false");
      countParams.set("returnCountOnly", "true");
      [pageData, total] = await Promise.all([
        postForm(queryUrl, pageParams, signal),
        postForm(queryUrl, countParams, signal).then(data => Number(data.count) || 0),
      ]);
    } else {
      const idsParams = new URLSearchParams(base);
      idsParams.set("returnGeometry", "false");
      idsParams.set("returnIdsOnly", "true");
      const idsData = await postForm(queryUrl, idsParams, signal);
      const ids = idsData.objectIds || [];
      total = ids.length;
      const pageIds = ids.slice(offset, offset + count);
      if (!pageIds.length) pageData = { features: [] };
      else {
        const pageParams = new URLSearchParams({ f: "json", objectIds: pageIds.join(","), outFields: "*", returnGeometry: "true", outSR: "3857" });
        if (maxAllowableOffset) pageParams.set("maxAllowableOffset", String(maxAllowableOffset));
        pageData = await postForm(queryUrl, pageParams, signal);
      }
    }
    const features = (pageData.features || []).map(feature => ({
      ...(feature.attributes || {}),
      geometry: esriGeometryToGeoJSON(feature.geometry),
      bbox: featureExtent(feature.geometry),
    }));
    return { features, total };
  },

  buildMapFilterParams(layer, appliedFilter, metadata) {
    if (metadata.provider === IMPORT_PROVIDER) {
      return { local: true, mapWarning: null };
    }
    const compiled = this.buildProviderFilter(appliedFilter, metadata);
    const crossLayer = activeAdvancedParts(appliedFilter).crossLayer;
    const artifact = crossLayer ? this.getCrossLayerArtifact(layer, appliedFilter) : null;
    if (metadata.provider === "geoserver") {
      const clauses = [];
      if (compiled.attribute) clauses.push(compiled.attribute);
      if (compiled.roi) {
        clauses.push(`${geoserverSpatialPredicate(compiled.roi.operation)}(${metadata.geometryField}, SRID=3857;${polygonToWkt(roiRings3857(compiled.roi))})`);
      }
      if (crossLayer) {
        if (artifact && artifact.empty) clauses.push("EXCLUDE");
        // Reusa los IDs ya resueltos por fetchGeoServer (misma corrida de
        // applyCandidate/syncMapFilter) en vez de repetir la cláusula
        // espacial completa en el CQL_FILTER del WMS — sin esto, cada tile
        // del mapa mientras se hace pan/zoom volvía a mandar el filtro
        // pesado (ver resolveGeoServerCrossLayerIds).
        else if (artifact && artifact.geoserverCrossLayerIds) {
          clauses.push(artifact.geoserverCrossLayerIds.length
            ? `IN (${artifact.geoserverCrossLayerIds.map(id => `'${id}'`).join(", ")})`
            : "EXCLUDE");
        } else if (isTerrainLayer(layer)) {
          // Ventana de carrera: el artifact todavía no terminó de resolverse
          // vía resolveGeoServerCrossLayerIds (Mongo nativo). A diferencia de
          // una capa GeoServer genérica, Terreno NUNCA puede expresar este
          // filtro como CQL_FILTER (gs-mongodb rechaza MultiPolygon de 2+
          // partes) — mejor no mostrar nada un instante que reconstruir la
          // descomposición hole-aware que se eliminó en PB-24_v4.
          clauses.push("EXCLUDE");
        } else {
          clauses.push(crossLayerSpatialClause(geoserverSpatialPredicate(crossLayer.operation), metadata.geometryField, artifact));
        }
      }
      return { cqlFilter: clauses.length ? clauses.map(c => `(${c})`).join(" AND ") : null, spatialFilter: null, mapWarning: null };
    }
    if (crossLayer) {
      const ids = (artifact && artifact.arcgisSourceIds) || [];
      const idClause = ids.length ? `${metadata.objectIdField} IN (${ids.join(",")})` : "1=0";
      if (metadata.serviceType === "featureserver") {
        return { featureWhere: idClause, layerDefs: null, spatialFilter: null, mapWarning: null };
      }
      return { layerDefs: JSON.stringify({ [metadata.layerId]: idClause }), spatialFilter: null, mapWarning: null };
    }
    if (metadata.serviceType !== "mapserver") {
      return {
        featureWhere: compiled.attribute || "1=1",
        layerDefs: null,
        spatialFilter: null,
        mapWarning: compiled.roi ? "La tabla se filtró correctamente, pero esta capa FeatureServer no admite sincronizar la región de interés sin un filtro entre capas." : null,
      };
    }
    const layerDefs = compiled.attribute ? JSON.stringify({ [metadata.layerId]: compiled.attribute }) : null;
    if (compiled.roi && metadata.supportsSpatialFilter) {
      return {
        layerDefs,
        spatialFilter: JSON.stringify({
          spatialRel: arcgisSpatialRelationship(compiled.roi.operation),
          geometryType: "esriGeometryPolygon",
          geometry: arcgisGeometry(compiled.roi),
        }),
        mapWarning: null,
      };
    }
    return {
      layerDefs,
      spatialFilter: null,
      mapWarning: compiled.roi ? "La tabla se filtró correctamente; este MapServer no soporta el filtro espacial exacto en la imagen del mapa." : null,
    };
  },

  async openTable(layer, recoveringCrossLayer = false) {
    store.dispatch("setMapToolSelection", { tool: "attributesTable", active: true });
    await store.dispatch("attributeTable/openTable", { layer, attributes: [], totalFeatures: 0 });
    try {
      const metadata = await this.getLayerMetadata(layer);
      await store.dispatch("attributeTable/setFieldMetadata", { schema: metadata.schema, aliases: metadata.aliases, metadata });
      if (metadata.empty) {
        // Capa_Terreno todavía sin respuestas aprobadas: mostrar la tabla
        // vacía (columnas ya quedaron seteadas arriba) en vez de intentar
        // consultar un feature type que GeoServer nunca provisionó.
        await store.dispatch("attributeTable/updateAttributes", { attributes: [], totalFeatures: 0 });
        await store.dispatch("attributeTable/setTotalUnfiltered", 0);
        return;
      }
      let appliedFilter = store.getters["attributeTable/appliedFilter"] || emptyAppliedFilter();
      try {
        this.buildProviderFilter(appliedFilter, metadata);
      } catch (validationError) {
        appliedFilter = emptyAppliedFilter();
        await store.dispatch("attributeTable/commitAppliedFilter", appliedFilter);
        store.dispatch("toast/show", { message: "El filtro guardado ya no es compatible con el esquema actual y fue descartado.", type: "warning" });
      }
      const result = await this.fetchAttributes(layer, { offset: 0, appliedFilter, metadata });
      if (result.features.length) {
        const schema = { ...metadata.schema };
        Object.keys(result.features[0]).forEach(key => {
          if (!["bbox", "geometry"].includes(key) && !schema[key]) schema[key] = inferType(result.features[0][key]);
        });
        metadata.schema = schema;
        await store.dispatch("attributeTable/setFieldMetadata", { schema, aliases: metadata.aliases, metadata });
      }
      await store.dispatch("attributeTable/updateAttributes", { attributes: result.features, totalFeatures: result.total });
      this.refreshTotalUnfiltered(layer, metadata);
    } catch (error) {
      console.error("Error abriendo tabla de atributos:", error);
      const current = store.getters["attributeTable/appliedFilter"] || emptyAppliedFilter();
      if (!recoveringCrossLayer && activeAdvancedParts(current).crossLayer) {
        setAttributeFilterEnabled(layer.id, false);
        store.dispatch("toast/show", { message: `${error.message || "No se pudo restaurar el filtro entre capas"} La configuración se conservó desactivada.`, type: "warning" });
        return this.openTable(layer, true);
      }
      store.dispatch("toast/show", { message: error.message || "No se pudieron cargar los atributos", type: "error" });
    } finally {
      await store.dispatch("attributeTable/setLoading", false);
    }
  },

  async updateTable(layer, { offset = 0, appliedFilter, metadata, signal, mapId } = {}) {
    const result = await this.fetchAttributes(layer, { offset, appliedFilter, metadata, signal, mapId });
    await store.dispatch("attributeTable/updateAttributes", { attributes: result.features, totalFeatures: result.total });
    return result;
  },

  async toggleSavedFilter(layer) {
    const savedFilter = readSavedAttributeFilter(layer.id);
    if (!savedFilter) {
      await this.openTable(layer);
      return { configured: false, enabled: false };
    }
    const enabled = !isAttributeFilterEnabled(layer.id);
    if (enabled) this.invalidateCrossLayer(layer, savedFilter);
    setAttributeFilterEnabled(layer.id, enabled);
    const appliedFilter = effectiveAttributeFilter(layer.id);
    const metadata = await this.applyFilterToMap(layer, appliedFilter);

    const currentLayer = store.getters["attributeTable/layer"];
    if (currentLayer && currentLayer.id === layer.id) {
      const result = await this.fetchAttributes(layer, { offset: 0, appliedFilter, metadata });
      await store.dispatch("attributeTable/setEffectiveFilter", appliedFilter);
      await store.dispatch("attributeTable/updateAttributes", { attributes: result.features, totalFeatures: result.total });
    }
    return { configured: true, enabled };
  },

  async applySavedFilterToMap(layer) {
    if (!isAttributeFilterEnabled(layer.id)) return null;
    const filter = effectiveAttributeFilter(layer.id);
    this.invalidateCrossLayer(layer, filter);
    return this.applyFilterToMap(layer, filter);
  },

  // applySavedFilterPreset carga un filtro guardado (preset de
  // store/modules/savedFilters.js) como el filtro activo de layer — único
  // punto de entrada usado tanto por el `<select>` de carga rápida en
  // ListPlegable.vue como por "Cargar" en el modal de filtros guardados de
  // AttributesTable.vue. A diferencia de openTable (que restaura el filtro
  // activo y lo descarta silenciosamente si ya no compila, porque la tabla
  // debe abrir igual), acá es una acción explícita del usuario: si el preset
  // ya no es compatible con el esquema vigente, se aborta SIN tocar el
  // filtro que estuviera aplicado — el llamador decide cómo avisar.
  async applySavedFilterPreset(layer, preset) {
    const applied = migrateAppliedFilter(preset.filter);
    const metadata = await this.getLayerMetadata(layer);
    try {
      this.buildProviderFilter(applied, metadata);
    } catch (validationError) {
      const error = new Error(validationError.message || "El filtro guardado ya no es compatible con el esquema actual");
      error.savedFilterIncompatible = true;
      error.preset = preset;
      throw error;
    }
    this.invalidateCrossLayer(layer, applied);
    writeSavedAttributeFilter(layer.id, applied);
    setAttributeFilterEnabled(layer.id, true);
    await this.applyFilterToMap(layer, applied);

    const currentLayer = store.getters["attributeTable/layer"];
    if (currentLayer && currentLayer.id === layer.id) {
      const result = await this.fetchAttributes(layer, { offset: 0, appliedFilter: applied, metadata });
      await store.dispatch("attributeTable/setEffectiveFilter", applied);
      await store.dispatch("attributeTable/updateAttributes", { attributes: result.features, totalFeatures: result.total });
    }
  },

  // captureAppliedFilter: "guardar" siempre significa "lo que está aplicado
  // ahora mismo" (rápido o avanzado) — prioriza el filtro persistido en
  // sessionStorage (fuente de verdad de "qué está activo", ver
  // attributeFilterState.js) y cae al filtro en memoria del store cuando la
  // tabla de esta capa nunca llegó a persistir nada (p.ej. recién abierta).
  // Devuelve null si no hay predicado real que guardar.
  captureAppliedFilter(layer) {
    const applied = readSavedAttributeFilter(layer.id) || store.getters["attributeTable/appliedFilter"];
    return hasAppliedPredicate(applied) ? cloneFilter(applied) : null;
  },

  // matchesAppliedFilter: para saber cuál preset está cargado ahora mismo en
  // una capa sin duplicar estado (ListPlegable.vue/AttributesTable.vue usan
  // esto para resaltar la opción activa en vez de guardar un id aparte que
  // podría desincronizarse).
  matchesAppliedFilter(layerId, preset) {
    const active = readSavedAttributeFilter(layerId);
    if (!active) return false;
    return JSON.stringify(migrateAppliedFilter(preset.filter)) === JSON.stringify(active);
  },

  async applyFilterToMap(layer, appliedFilter) {
    const metadata = await this.getLayerMetadata(layer);
    const artifact = await this.prepareCrossLayer(layer, appliedFilter);
    if (artifact && metadata.provider === "arcgis") {
      await this.resolveArcgisSourceIds(layer, appliedFilter, metadata, artifact);
    }
    if (metadata.provider === IMPORT_PROVIDER) this.syncImportedMapFilter(layer, appliedFilter, metadata);
    else mapService.updateLayerFilter(layer.id, this.buildMapFilterParams(layer, appliedFilter, metadata));
    return metadata;
  },

  validateFilters(filters, schema = {}) {
    const errors = {};
    Object.entries(filters || {}).forEach(([field, condition]) => {
      if (!isConditionActive(condition)) return;
      const values = condition.operator === "in" ? condition.value : [condition.value];
      if (condition.operator === "in" && values.length > 50) errors[field] = "Máximo 50 valores";
      if (schema[field] === "number" && values.some(value => !Number.isFinite(Number(value)))) errors[field] = "Ingrese un número válido";
      if (schema[field] === "date" && values.some(value => Number.isNaN(Date.parse(value)))) errors[field] = "Ingrese una fecha válida";
    });
    return errors;
  },

  // Compatibilidad para consumidores del filtro rápido anterior.
  generateCQLFilter(filters, schema = {}) { return compileQuickFilters(filters, "geoserver", schema) || "INCLUDE"; },
  generateArcGISWhereClause(filters, schema = {}) { return compileQuickFilters(filters, "arcgis", schema) || "1=1"; },
  buildWMSFilterParams(layer, filters) {
    const metadata = store.getters["attributeTable/metadata"];
    return metadata ? this.buildMapFilterParams(layer, quickAppliedFilter(filters), metadata) : {};
  },

  // #76: nombre personalizado del archivo descargado; vacío mantiene el
  // predeterminado y los espacios se normalizan a guion bajo
  resolveDownloadFilename(customName, defaultName) {
    const trimmed = (customName || "").trim();
    return trimmed ? trimmed.replace(/\s+/g, "_") : defaultName;
  },

  // QA-002: no duplicar la extensión si el nombre elegido ya la trae
  // (comparación sin distinguir mayúsculas); una extensión de otro formato es
  // parte del nombre elegido por el usuario y se conserva
  withDownloadExtension(filename, extension) {
    if (filename.toLowerCase().endsWith(`.${extension.toLowerCase()}`)) return filename;
    return `${filename}.${extension}`;
  },

  async fetchAllPages(layer, { appliedFilter, metadata, cap }) {
    const features = [];
    let offset = 0; let total = 0;
    while (features.length < cap) {
      const count = Math.min(2000, cap - features.length);
      const result = await this.fetchAttributes(layer, { offset, count, appliedFilter: appliedFilter || emptyAppliedFilter(), metadata });
      features.push(...result.features);
      total = result.total;
      offset += result.features.length;
      if (!result.features.length || offset >= total) break;
    }
    return { features, total };
  },

  // Zoom a resultados (AttributesTable.vue::zoomToResults) necesita el bbox
  // agregado de TODAS las coincidencias, no solo de la página cargada en la
  // tabla (RECORDS_PER_PAGE) — mismo patrón de paginación que downloadFeatures,
  // con un tope más bajo (MAX_ZOOM_EXTENT_RECORDS) porque acá bloquea la UI.
  async fetchFeaturesForExtent(layer, appliedFilter, metadata) {
    return this.fetchAllPages(layer, { appliedFilter, metadata, cap: MAX_ZOOM_EXTENT_RECORDS });
  },

  async downloadFeatures(layer, appliedFilter, formats, customName, kmlNameField) {
    store.dispatch("toast/show", { message: "Iniciando descarga...", type: "info" });
    try {
      const metadata = await this.getLayerMetadata(layer);
      const { features, total } = await this.fetchAllPages(layer, { appliedFilter, metadata, cap: MAX_DOWNLOAD_RECORDS });
      if (!features.length) {
        store.dispatch("toast/show", { message: "No hay datos para descargar", type: "warning" });
        return;
      }
      if (total > MAX_DOWNLOAD_RECORDS) store.dispatch("toast/show", { message: `Descarga limitada a los primeros ${MAX_DOWNLOAD_RECORDS} registros`, type: "warning" });
      const filename = this.resolveDownloadFilename(customName, `${layer.name.replace(/\s+/g, "_")}_${Date.now()}`);
      const needsGeoJSON = formats.some(format => ["geojson", "shp", "kmz"].includes(format));
      const geojson = needsGeoJSON ? this.toGeoJSON(features) : null;
      // filterDescriptionLines: detalle del filtro aplicado, para adjuntar en
      // los formatos que lo admiten de forma nativa (CSV: filas de preámbulo;
      // GeoJSON: propiedad "metadata" a nivel de FeatureCollection; KMZ:
      // <description> del Document). SHP no tiene un campo de metadata a
      // nivel de capa (solo atributos por feature en el .dbf), así que se
      // omite ahí — no se agrega un archivo aparte dentro del zip.
      const filterDescriptionLines = describeAppliedFilter(appliedFilter, metadata.aliases);
      for (const format of formats) {
        if (format === "csv") {
          const headers = Object.keys(features[0]).filter(key => !["bbox", "geometry"].includes(key));
          let csv = this.jsonToCsv(features, headers);
          if (filterDescriptionLines) {
            const preamble = filterDescriptionLines.map(line => `"${line.replace(/"/g, '""')}"`).join("\r\n");
            csv = `${preamble}\r\n\r\n${csv}`;
          }
          this.triggerDownload(this.withDownloadExtension(filename, "csv"), csv, "text/csv;charset=utf-8;");
        } else if (format === "geojson") {
          const output = filterDescriptionLines ? { ...geojson, metadata: { filtroAplicado: filterDescriptionLines.join("\n") } } : geojson;
          this.triggerDownload(this.withDownloadExtension(filename, "geojson"), JSON.stringify(output), "application/json;charset=utf-8;");
        } else if (format === "shp") {
          const compatible = geojson.features.filter(feature => feature.geometry && ["Point", "LineString", "Polygon", "MultiLineString", "MultiPolygon"].includes(feature.geometry.type));
          if (!compatible.length) { store.dispatch("toast/show", { message: "No hay geometrías compatibles con Shapefile", type: "error" }); continue; }
          this.triggerDownload(this.withDownloadExtension(filename, "zip"), await this.toSHP({ ...geojson, features: compatible }), "application/zip");
        } else if (format === "kmz") {
          this.triggerDownload(this.withDownloadExtension(filename, "kmz"), await this.toKMZ(geojson, filterDescriptionLines ? filterDescriptionLines.join("\n") : null, kmlNameField), "application/vnd.google-earth.kmz");
        }
      }
      store.dispatch("toast/show", { message: "Descarga completada", type: "success" });
    } catch (error) {
      console.error("Error downloading features:", error);
      store.dispatch("toast/show", { message: error.message || "Error al descargar los datos", type: "error" });
      throw error;
    }
  },

  jsonToCsv(items, headers) {
    return [headers.join(","), ...items.map(row => headers.map(field => `"${String(row[field] == null ? "" : row[field]).replace(/"/g, '""')}"`).join(","))].join("\r\n");
  },
  transformGeometry(geometry, from, to) {
    if (!geometry) return null;
    const result = cloneFilter(geometry);
    const tx = coordinate => transform(coordinate, from, to);
    if (result.type === "Point") result.coordinates = tx(result.coordinates);
    else if (["LineString", "MultiPoint"].includes(result.type)) result.coordinates = result.coordinates.map(tx);
    else if (["Polygon", "MultiLineString"].includes(result.type)) result.coordinates = result.coordinates.map(ring => ring.map(tx));
    else if (result.type === "MultiPolygon") result.coordinates = result.coordinates.map(polygon => polygon.map(ring => ring.map(tx)));
    return result;
  },
  toGeoJSON(features) {
    return { type: "FeatureCollection", features: features.map(item => {
      const properties = { ...item }; delete properties.bbox; delete properties.geometry;
      return { type: "Feature", geometry: this.transformGeometry(item.geometry, "EPSG:3857", "EPSG:4326"), properties };
    }) };
  },
  // kmlNameField: ol/format/KML solo toma el <name> del Placemark de una
  // propiedad literalmente llamada "name" — si la capa no tiene una columna
  // con ese nombre exacto, todos los marcadores salen sin título. Acá se
  // copia el valor de la columna elegida por el usuario a "name" en un
  // GeoJSON clonado (no se toca el `geojson` compartido con los otros
  // formatos de la descarga).
  async toKMZ(geojson, descriptionText, kmlNameField) {
    const source = kmlNameField
      ? { ...geojson, features: geojson.features.map(feature => ({ ...feature, properties: { ...feature.properties, name: feature.properties[kmlNameField] } })) }
      : geojson;
    const features = new GeoJSON().readFeatures(source, { dataProjection: "EPSG:4326", featureProjection: "EPSG:4326" });
    let kml = new KML().writeFeatures(features, { dataProjection: "EPSG:4326", featureProjection: "EPSG:4326" });
    if (descriptionText) kml = this.injectKmlDescription(kml, descriptionText);
    const zip = new JSZip(); zip.file("doc.kml", kml);
    return zip.generateAsync({ type: "blob", mimeType: "application/vnd.google-earth.kmz" });
  },
  // injectKmlDescription: agrega el detalle del filtro aplicado como
  // <description> del <Document> del KML (campo nativo de KML, visible en
  // Google Earth/QGIS al abrir la capa) — se parsea/serializa con
  // DOMParser/XMLSerializer en vez de tocar el string a mano para no
  // arriesgar un XML mal formado si ol/format/KML cambia su salida. Si el
  // KML no trae <Document> (ol solo lo agrega en ciertas condiciones), se
  // crea uno envolviendo los Placemark existentes.
  injectKmlDescription(kmlString, descriptionText) {
    const ns = "http://www.opengis.net/kml/2.2";
    const doc = new DOMParser().parseFromString(kmlString, "text/xml");
    const kmlEl = doc.getElementsByTagName("kml")[0];
    if (!kmlEl) return kmlString;
    let documentEl = doc.getElementsByTagName("Document")[0];
    if (!documentEl) {
      documentEl = doc.createElementNS(ns, "Document");
      while (kmlEl.firstChild) documentEl.appendChild(kmlEl.firstChild);
      kmlEl.appendChild(documentEl);
    }
    const descriptionEl = doc.createElementNS(ns, "description");
    descriptionEl.textContent = descriptionText;
    documentEl.insertBefore(descriptionEl, documentEl.firstChild);
    return new XMLSerializer().serializeToString(doc);
  },
  async toSHP(geojson) { return shpwrite.zip(geojson, { outputType: "blob", compression: "DEFLATE" }); },
  triggerDownload(filename, content, mimeType) {
    const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
    if (navigator.msSaveBlob) { navigator.msSaveBlob(blob, filename); return; }
    const url = URL.createObjectURL(blob); const link = document.createElement("a");
    link.href = url; link.download = filename; link.style.visibility = "hidden";
    document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
  },
};
